import { useRef, useState } from 'react';
import { FormIcon } from './FormIcon.jsx';
import { LAYOUT_TYPES, formatBytes } from './formSchema.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const INPUT_TYPES = { text: 'text', email: 'email', phone: 'tel', date: 'date', url: 'url' };

function acceptAttr(types) {
  return types.length ? types.map((t) => `.${t}`).join(',') : undefined;
}

function FileDrop({ field, files, onFilesChange, disabled, maxMb, allowedTypes }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const types = field.accept?.length ? field.accept : allowedTypes;
  const limit = field.max_mb || maxMb;

  const add = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    onFilesChange(field.multiple ? [...files, ...incoming].slice(0, 10) : incoming.slice(0, 1));
  };

  return (
    <div>
      <div
        className={`fm-drop${over ? ' is-over' : ''}${disabled ? ' is-disabled' : ''}`}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!disabled) add(e.dataTransfer.files);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <FormIcon name="file" size={26} />
        <span>
          Drag and drop files here or <span className="fm-link">browse files</span>
        </span>
        <small>
          {types.length ? types.slice(0, 6).map((t) => t.toUpperCase()).join(', ') : 'Any file'}
          {types.length > 6 ? '…' : ''} (Max {limit}MB)
        </small>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple={field.multiple}
          accept={acceptAttr(types)}
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {files.length ? (
        <ul className="fm-file-list">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`}>
              <FormIcon name="forms" size={15} />
              <span>{file.name}</span>
              <small>{formatBytes(file.size)}</small>
              {!disabled ? (
                <button type="button" className="fm-icon-btn" aria-label={`Remove ${file.name}`} onClick={() => onFilesChange(files.filter((_, i) => i !== index))}>
                  <FormIcon name="x" size={14} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Respondent-facing control for one field. Used by the builder canvas, preview, public page and client portal.
 */
export function FieldControl({ field, value, onChange, files = [], onFilesChange = () => {}, disabled = false, maxMb = 10, allowedTypes = [], error = '' }) {
  if (field.type === 'heading') {
    return (
      <div className="fm-r-heading">
        <h3>{field.label}</h3>
        {field.help ? <p>{field.help}</p> : null}
      </div>
    );
  }
  if (field.type === 'divider') return <hr className="fm-r-divider" />;
  if (field.type === 'paragraph') return <p className="fm-r-paragraph">{field.text || field.label}</p>;

  const id = `fm-${field.id || field.key}`;
  const singleCheckbox = field.type === 'checkbox' && !field.options?.length;
  let control;

  switch (field.type) {
    case 'textarea':
      control = <textarea id={id} rows={4} value={value || ''} placeholder={field.placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'select':
      control = (
        <div className="fm-select-wrap">
          <SearchableSelect id={id} value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
            <option value="">{field.placeholder || 'Select an option'}</option>
            {field.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </SearchableSelect>
          <FormIcon name="chevronDown" size={16} />
        </div>
      );
      break;
    case 'radio':
      control = (
        <div className="fm-choices" role="radiogroup" aria-labelledby={`${id}-label`}>
          {field.options.map((o) => (
            <label key={o} className={`fm-choice${value === o ? ' is-on' : ''}`}>
              <input type="radio" name={id} checked={value === o} disabled={disabled} onChange={() => onChange(o)} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
      break;
    case 'checkbox':
      if (singleCheckbox) {
        control = (
          <label className={`fm-choice${value ? ' is-on' : ''}`}>
            <input type="checkbox" checked={Boolean(value)} disabled={disabled} onChange={(e) => onChange(e.target.checked ? true : '')} />
            <span>{field.placeholder || 'Yes'}</span>
          </label>
        );
      } else {
        const list = Array.isArray(value) ? value : [];
        control = (
          <div className="fm-choices">
            {field.options.map((o) => (
              <label key={o} className={`fm-choice${list.includes(o) ? ' is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={list.includes(o)}
                  disabled={disabled}
                  onChange={(e) => onChange(e.target.checked ? [...list, o] : list.filter((x) => x !== o))}
                />
                <span>{o}</span>
              </label>
            ))}
          </div>
        );
      }
      break;
    case 'file':
      control = <FileDrop field={field} files={files} onFilesChange={onFilesChange} disabled={disabled} maxMb={maxMb} allowedTypes={allowedTypes} />;
      break;
    case 'url':
      control = (
        <div className="fm-input-icon">
          <FormIcon name="globe" size={16} />
          <input id={id} type="url" value={value || ''} placeholder={field.placeholder || 'https://'} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        </div>
      );
      break;
    default:
      control = (
        <input
          id={id}
          type={INPUT_TYPES[field.type] || 'text'}
          value={value || ''}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }

  return (
    <div className={`fm-r-field${error ? ' has-error' : ''}`}>
      <label className="fm-r-label" id={`${id}-label`} htmlFor={['radio', 'checkbox', 'file'].includes(field.type) ? undefined : id}>
        {field.label}
        {field.required ? <span className="fm-req">*</span> : null}
      </label>
      {control}
      {field.help ? <p className="fm-r-help">{field.help}</p> : null}
      {error ? <p className="fm-r-error">{error}</p> : null}
    </div>
  );
}

export function FormBanner({ title, description }) {
  return (
    <div className="fm-banner">
      <h2>{title || 'Untitled form'}</h2>
      {description ? <p>{description}</p> : null}
    </div>
  );
}

function isEmpty(field, value, files) {
  if (field.type === 'file') return !files?.length;
  if (field.type === 'checkbox') return field.options?.length ? !(Array.isArray(value) && value.length) : !value;
  return value === undefined || value === null || String(value).trim() === '';
}

function serverErrors(body) {
  const out = {};
  Object.entries(body?.errors || {}).forEach(([key, messages]) => {
    const match = key.match(/^(?:payload|files)\.([^.]+)/);
    if (match) out[match[1]] = Array.isArray(messages) ? messages[0] : String(messages);
  });
  return out;
}

export function buildSubmissionBody(values, files, extra = {}) {
  const body = new FormData();
  body.append('payload', JSON.stringify(values));
  Object.entries(files).forEach(([key, list]) => {
    (list || []).forEach((file) => body.append(`files[${key}][]`, file));
  });
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null) body.append(key, String(value));
  });
  return body;
}

/**
 * @param {{
 *   definition: { title: string; description?: string; fields: any[]; submit_label?: string; success_message?: string; banner?: boolean; honeypot?: boolean; max_file_mb?: number; allowed_file_types?: string[] };
 *   onSubmit?: (values: object, files: object, honeypot: string) => Promise<{ message?: string; redirect_url?: string } | void>;
 *   disabled?: boolean;
 *   initialValues?: object;
 *   notice?: import('react').ReactNode;
 * }} props
 */
export function FormRenderer({ definition, onSubmit, disabled = false, initialValues = {}, notice = null }) {
  const [values, setValues] = useState(initialValues);
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [hp, setHp] = useState('');

  const fields = definition.fields || [];

  const submit = async (event) => {
    event.preventDefault();
    if (disabled || !onSubmit) return;
    const nextErrors = {};
    fields.forEach((field) => {
      if (LAYOUT_TYPES.includes(field.type)) return;
      if (field.required && isEmpty(field, values[field.key], files[field.key])) nextErrors[field.key] = 'This field is required.';
    });
    setErrors(nextErrors);
    setFormError('');
    if (Object.keys(nextErrors).length) {
      setFormError('Please fill in the highlighted fields.');
      return;
    }
    setBusy(true);
    try {
      const result = await onSubmit(values, files, hp);
      if (result?.redirect_url) {
        window.top.location.href = result.redirect_url;
        return;
      }
      setDone(result?.message || definition.success_message || 'Thanks! Your response was submitted.');
    } catch (err) {
      const mapped = serverErrors(err.body);
      setErrors(mapped);
      setFormError(err.message || 'Could not submit the form.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="fm-render">
        {definition.banner !== false ? <FormBanner title={definition.title} description={definition.description} /> : null}
        <div className="fm-success">
          <span className="fm-success-icon">
            <FormIcon name="check" size={26} strokeWidth={2.4} />
          </span>
          <h3>Submitted</h3>
          <p>{done}</p>
        </div>
      </div>
    );
  }

  return (
    <form className="fm-render" onSubmit={submit} noValidate>
      {definition.banner !== false ? <FormBanner title={definition.title} description={definition.description} /> : null}
      <div className="fm-render-body">
        {notice}
        {fields.map((field) => (
          <FieldControl
            key={field.id || field.key}
            field={field}
            value={values[field.key]}
            onChange={(v) => setValues((cur) => ({ ...cur, [field.key]: v }))}
            files={files[field.key] || []}
            onFilesChange={(list) => setFiles((cur) => ({ ...cur, [field.key]: list }))}
            disabled={disabled || busy}
            maxMb={definition.max_file_mb || 10}
            allowedTypes={definition.allowed_file_types || []}
            error={errors[field.key]}
          />
        ))}
        {definition.honeypot ? (
          <input className="fm-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={hp} onChange={(e) => setHp(e.target.value)} name="company_website" />
        ) : null}
        {formError ? <p className="fm-form-error">{formError}</p> : null}
        <button type="submit" className="fm-btn fm-btn--blue fm-submit" disabled={disabled || busy || !onSubmit}>
          {busy ? 'Submitting…' : definition.submit_label || 'Submit'}
        </button>
      </div>
    </form>
  );
}
